import React from 'react';
import { Helmet } from 'react-helmet'; 
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCategoryText } from '../../../utils/seoHelpers';

export default function MaterialProfile({ materialData, relatedMaterials }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  
  const { 
    master_keyword, 
    hs_chapter, 
    slug,
    spec_template, 
    deep_content_en, 
    deep_content_fr
  } = materialData;

  const formattedKeyword = master_keyword
    .replace(/commercial Canada/i, 'in Canada') 
    .replace(/(^\w|\s\w)/g, m => m.toUpperCase()); 

  const isFrench = location.pathname.startsWith('/fr') || i18n.language?.startsWith('fr');
  
  // Pull the correct language JSONB matrix from Supabase
  const deepContent = isFrench ? (deep_content_fr || deep_content_en || {}) : (deep_content_en || deep_content_fr || {});

  // ⚡ DYNAMIC OVERVIEW: Pulls straight from database JSONB overview key, falls back gracefully
  const seoTextContent = deepContent.overview || getCategoryText(hs_chapter);

  const getTableData = (chapter) => {
    const chap = String(chapter);
    if (['72', '73'].includes(chap)) return { route: isFrench ? 'Québec ➔ Nouvelle-Écosse' : 'Quebec ➔ Nova Scotia', friction: isFrench ? 'Audits municipaux requis' : 'Municipal Audits Required', freight: 'LTL Class 50 (High Density)' };
    if (chap === '44') return { route: isFrench ? 'Colombie-Britannique ➔ Ontario' : 'British Columbia ➔ Ontario', friction: isFrench ? 'ALC standard (Sans exclusion)' : 'Standard CFTA (No Exclusions)', freight: 'LTL Class 70' };
    if (['25', '68'].includes(chap)) return { route: isFrench ? 'Rayon local (Intra-provincial)' : 'Local Radius (Intra-Provincial)', friction: isFrench ? 'Limites environnementales / de poids' : 'Environmental / Weight Limits', freight: 'LTL Class 55' };
    return { route: 'Ontario ➔ Alberta', friction: 'Standard CFTA Verification', freight: 'LTL Class 60' };
  };

  const tableData = getTableData(hs_chapter);

  return (
    <div className="min-h-screen bg-gray-50 font-sans pb-16">
      
      <Helmet>
        <title>{isFrench ? `Approvisionnement direct pour ${formattedKeyword} | TariffBeater` : `Direct Sourcing For ${formattedKeyword} | TariffBeater`}</title>
        <meta name="description" content={`Find regional fabricators and bypass distributor markups for ${formattedKeyword}. Ensure CFTA compliance and optimize your domestic supply chain.`} />
        <meta property="og:title" content={`Direct Sourcing For ${formattedKeyword} | TariffBeater`} />
        <meta property="og:url" content={`https://www.tariffbeater.com${location.pathname}`} />
      </Helmet>

      <main className="max-w-5xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight mb-4">
            {isFrench ? `Approvisionnement direct pour ${formattedKeyword}` : `Direct Sourcing for ${formattedKeyword}`}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {isFrench 
              ? "Évitez les marges des grands distributeurs. Connectez-vous directement avec les fabricants canadiens et calculez des coûts rendus interprovinciaux précis."
              : "Bypass big-box distributor markups. Connect directly with Canadian fabricators and calculate accurate inter-provincial landed costs."}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col h-full">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-blue-600">🏛️</span> {isFrench ? "Aperçu du marché et de la conformité" : "Market & Compliance Overview"}
            </h2>
            <p className="text-gray-700 leading-relaxed flex-grow">
              {seoTextContent}
            </p>
            <div className="mt-6 pt-6 border-t border-gray-100">
               <p className="text-sm text-gray-500">
                 <strong>{isFrench ? "Code du chapitre SH :" : "HS Chapter Code:"}</strong> {hs_chapter}
               </p>
            </div>
          </div>

          <div className="bg-slate-900 p-8 rounded-2xl shadow-lg border border-slate-800 text-white flex flex-col h-full">
            <h2 className="text-lg font-bold mb-6 flex items-center gap-2">
              <span>📊</span> {isFrench ? "Profil d'approvisionnement type" : "Sample Sourcing Profile"}
            </h2>
            <dl className="space-y-4 text-sm flex-grow">
              <div className="flex justify-between border-b border-slate-700 pb-2">
                <dt className="text-slate-400">{isFrench ? "Itinéraire type" : "Sample Route"}</dt>
                <dd className="font-semibold text-right">{tableData.route}</dd>
              </div>
              <div className="flex justify-between border-b border-slate-700 pb-2">
                <dt className="text-slate-400">{isFrench ? "Friction ALC" : "CFTA Friction"}</dt>
                <dd className="font-semibold text-amber-400 text-right">{tableData.friction}</dd>
              </div>
              <div className="flex justify-between pb-2">
                <dt className="text-slate-400">{isFrench ? "Profil de transport est." : "Est. Transport Profile"}</dt>
                <dd className="font-semibold text-blue-300 text-right">{tableData.freight}</dd>
              </div>
            </dl>

            <button 
              onClick={() => navigate(
                isFrench ? `/fr/rfq/new?item=${encodeURIComponent(master_keyword)}&chapter=${hs_chapter}` : `/rfq/new?item=${encodeURIComponent(master_keyword)}&chapter=${hs_chapter}`,
                { state: { specTemplate: spec_template } }
              )}
              className="mt-8 w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 px-4 rounded-xl transition-colors shadow-md text-lg cursor-pointer"
            >
              {isFrench ? "Demander un devis national (Gratuit)" : "Request Domestic Quote (Free)"}
            </button>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-4 text-center">
              {isFrench ? "Identité masquée jusqu'à l'acceptation de l'offre" : "Identity Hidden Until Bid Acceptance"}
            </p>
          </div>
        </div>

        {(deepContent.specs || deepContent.logistics || deepContent.compliance) && (
          <div className="mt-8 space-y-8 max-w-5xl mx-auto text-gray-800">
            
            {deepContent.specs && (
              <section className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
                <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <span className="w-3 h-3 bg-blue-600 rounded-full"></span>
                  {isFrench ? "Estimation des coûts et planification des métrés" : "Cost Estimation & Takeoff Planning"}
                </h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                  {deepContent.specs}
                </p>
              </section>
            )}

            {deepContent.logistics && (
              <section className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
                <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <span className="w-3 h-3 bg-amber-600 rounded-full"></span>
                  {isFrench ? "Atténuation des tarifs et logistique régionale" : "Tariff Mitigation & Regional Logistics"}
                </h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                  {deepContent.logistics}
                </p>
              </section>
            )}

            {deepContent.compliance && (
              <section className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
                <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <span className="w-3 h-3 bg-emerald-600 rounded-full"></span>
                  {isFrench ? "Substitution locale vérifiée et conformité" : "Verified Domestic Substitution & Compliance"}
                </h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                  {deepContent.compliance}
                </p>
              </section>
            )}

          </div>
        )}

        {relatedMaterials && relatedMaterials.length > 0 && (
          <div className="mt-16 border-t border-gray-200 pt-12">
            <h3 className="text-xl font-bold text-gray-900 mb-6">{isFrench ? "Matériaux de structure apparentés" : "Related Core & Shell Materials"}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {relatedMaterials.map((item) => (
                <Link 
                  key={item.id} 
                  to={isFrench ? `/fr/materials/${item.slug}` : `/materials/${item.slug}`}
                  className="bg-white p-4 rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-md transition-all text-sm font-semibold text-gray-800"
                >
                  {isFrench ? (item.master_keyword_fr || item.master_keyword) : item.master_keyword}
                </Link>
              ))}
            </div>
          </div>
        )}

      </main>
    </div>
  );
}